import {
  fileParserQuery,
  fileParserPage
} from '../types';
import { Observable, of } from 'rxjs';

export function splitRow(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0, len = line.length; i < len; i++) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        field += '"';
        i++;
      } else inQuotes = !inQuotes;
      continue;
    }
    if (ch === delimiter && !inQuotes) {
      result.push(field);
      field = '';
      continue;
    }

    field += ch;
  }

  result.push(field);
  return result;
}

export function parseFile<T>(csv: string | ArrayBuffer, transformer: (headers: string[], cols: string[]) => T): T[] {
  const text = typeof csv === 'string' ? csv : new TextDecoder().decode(csv);
  const normalized = text.replace(/\r\n|\r/g, '\n').replace(/^\uFEFF/, '').trim();
  const [headerLine, ...bodyLines] = normalized.split('\n').filter(Boolean);

  if (!headerLine) return [];

  const delim = headerLine.includes(';') && !headerLine.includes(',') ? ';' : ',';
  const headers = splitRow(headerLine, delim).map(h => h.trim());

  return bodyLines
    .map(line => splitRow(line, delim).map(col => col.trim()))
    .filter(cols => !(cols.length === 1 && headers.length > 1))
    .map(cols => transformer(headers, cols));
}

export function loadFile<T>(file: File | null, transformer: (headers: string[], cols: string[]) => T): Promise<T[]> {
  return new Promise((resolve) => {
    if (!file) {
      resolve([]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const contents = event.target?.result;
      resolve(contents ? parseFile(contents, transformer) : []);
    };
    reader.readAsText(file);
  });
}

const mimeTypes: Record<string, string> = {
  csv: 'text/csv;charset=utf-8;',
  txt: 'text/plain;charset=utf-8;',
  json: 'application/json;charset=utf-8;',
};

export function downloadFile<T extends Record<string, any>>(
  fileName: string,
  data: T[],
  fileType: string,
): void {
  const mimeType = mimeTypes[fileType] || 'application/octet-stream';
  const content = fileType === 'json'
    ? JSON.stringify(data, null, 2)
    : data.map(row => Object.values(row).join(',')).join('\n');
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${fileName}.${fileType}`;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
}

export function pagelist<T>(result: T[], query: fileParserQuery): Observable<fileParserPage<T>> {
  if (query.sortField) {
    const field = query.sortField as keyof T;
    const direction = query.sortOrder === 'desc' ? -1 : 1;
    result.sort((a, b) => {
      const av = a[field];
      const bv = b[field];
      if (av < bv) return -1 * direction;
      if (av > bv) return 1 * direction;
      return 0;
    });
  }

  const total = result.length;
  const start = Math.max(0, query.first);
  const end = start + Math.max(1, query.rows);
  const data = result.slice(start, end);

  return of({ data, total }).pipe();
}